import { Info, MailCheck, MailX, Send } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import type { Translations } from "@/shared/lang/interfaces/translationTypes";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";

import type { WalletDebit, WalletDebitCreated, WalletDebitEmailStatus } from "../types/walletDebits.types";
import { SendFields } from "./WalletDebitDetailDrawer";
import { WalletDebitManage } from "./WalletDebitManage";
import { WalletDebitStatusPill } from "./WalletDebitStatusPill";

const EMAIL_STATUS: Record<
  Exclude<WalletDebitEmailStatus, "not_configured">,
  { icon: LucideIcon; tone: string; key: keyof Translations["walletDebits"] }
> = {
  pending: { icon: Send, tone: "text-muted-foreground", key: "email_pending" },
  sent: { icon: MailCheck, tone: "text-success", key: "email_sent" },
  failed: { icon: MailX, tone: "text-destructive", key: "email_failed" },
};

export interface WalletDebitSuccessProps {
  debit: WalletDebitCreated;
  /** Found by checking a debit whose answer was lost, so it was debited earlier, not now. */
  recovered?: boolean;
  onChange: (debit: WalletDebit) => void;
}

/**
 * The debit as it now stands: reserved and waiting for its send (recorded right here, once made), or already
 * sent; the internal notices, its proof and its timeline.
 */
export function WalletDebitSuccess({ debit, recovered = false, onChange }: WalletDebitSuccessProps) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  return (
    <div className="space-y-5">
      {recovered ? (
        <ColocaAlert variant="secondary" icon={Info} text={t("done_recovered")} />
      ) : (
        debit.replayed && <ColocaAlert variant="secondary" icon={Info} text={t("done_replayed")} />
      )}

      <section
        aria-label={t("done_receipt_label")}
        className="overflow-hidden rounded-2xl border border-border bg-card"
      >
        <div className="flex items-start justify-between gap-4 bg-muted/40 px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
              {t("done_receipt_label")}
            </p>
            <p className="mt-0.5 font-mono text-sm font-semibold text-foreground">{debit.reference}</p>
          </div>
          <WalletDebitStatusPill debit={debit} />
        </div>
        <div className="px-5 pt-4 pb-5">
          <p className="text-xs text-muted-foreground">{t("done_amount")}</p>
          <div className="mt-1 text-foreground">
            <ColocaAmountDisplay
              value={`-${debit.amount}`}
              currency={debit.currency}
              fractionDigits={minorUnitsFor(debit.currency)}
              size="xl"
              signed
            />
          </div>
          <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <ReceiptField label={t("done_organization")} value={debit.organization_name ?? "—"} />
            <ReceiptField
              label={t("done_wallet")}
              value={t("review_wallet_value", { currency: debit.currency })}
            />
            <SendFields debit={debit} locale={activeLanguage} />
            <ReceiptField label={t("done_concept")} value={debit.concept} />
          </dl>
        </div>
      </section>

      {debit.emails && (
        <ul className="space-y-2" aria-label={t("done_emails_label")}>
          <EmailLine status={debit.emails.operator} who={t("email_to_operator")} />
          <EmailLine status={debit.emails.accounting} who={t("email_to_accounting")} />
        </ul>
      )}

      <WalletDebitManage debit={debit} canEdit onChange={onChange} />
    </div>
  );
}

function ReceiptField({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("mt-0.5 break-words font-medium text-foreground", mono && "font-mono text-[13px]")}>
        {value}
      </dd>
    </div>
  );
}

function EmailLine({ status, who }: { status: WalletDebitEmailStatus; who: string }) {
  const { t } = useTranslations("walletDebits");
  if (status === "not_configured") return null;
  // A status this build does not know yet reads as on its way rather than breaking the screen.
  const { icon: Icon, tone, key } = EMAIL_STATUS[status] ?? EMAIL_STATUS.pending;
  return (
    <li className="flex items-start gap-2 text-sm text-foreground">
      <Icon className={cn("mt-0.5 size-4 shrink-0", tone)} aria-hidden="true" />
      <span>{t(key, { who })}</span>
    </li>
  );
}
